import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Building2, Plus, Trash2, Edit3, Check, X } from "lucide-react";

/**
 * Admin-only brand catalogue management. Add / rename / delete brands and
 * their keyword patterns. Rename cascades to inventory.firma manual
 * assignments; delete clears manual assignments referencing that brand.
 */
export default function Brands() {
  const { user } = useAuth();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newFirma, setNewFirma] = useState("");
  const [newKeywords, setNewKeywords] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingFirma, setEditingFirma] = useState(null);
  const [editValues, setEditValues] = useState({ firma: "", keywords: "" });

  const load = () => {
    setLoading(true);
    api
      .get("/stats/brands/list")
      .then(({ data }) => setBrands(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (["admin", "supervisor"].includes(user?.role)) load();
  }, [user]);

  if (!["admin", "supervisor"].includes(user?.role)) {
    return (
      <div className="hard border border-[color:var(--warning)] bg-[color:var(--warning-bg)] text-[color:var(--warning)] p-4">
        Bu sayfa yalnızca yöneticiler içindir.
      </div>
    );
  }

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/brands", {
        firma: newFirma,
        keywords: newKeywords
          .split(",")
          .map((k) => k.trim())
          .filter(Boolean),
      });
      toast.success(`"${newFirma.toUpperCase()}" firması eklendi`);
      setNewFirma("");
      setNewKeywords("");
      load();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (b) => {
    setEditingFirma(b.firma);
    setEditValues({
      firma: b.firma,
      keywords: (b.keywords || []).join(", "),
    });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/brands/${encodeURIComponent(editingFirma)}`, {
        firma: editValues.firma,
        keywords: editValues.keywords
          .split(",")
          .map((k) => k.trim())
          .filter(Boolean),
      });
      toast.success("Firma güncellendi");
      setEditingFirma(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (firma) => {
    if (
      !window.confirm(
        `"${firma}" firmasını silmek istiyor musunuz? Bu markaya manuel atanan tüm ürünlerin firması boşalacak.`,
      )
    )
      return;
    try {
      await api.delete(`/brands/${encodeURIComponent(firma)}`);
      toast.success("Firma silindi");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-4">
      <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)]">
        <div className="p-4 border-b border-[color:var(--border)] flex items-center gap-2">
          <Building2 size={18} />
          <h3 className="font-bold">Firmalar</h3>
          <span className="ticker text-[color:var(--text-3)] ml-auto">
            {brands.length} firma
          </span>
        </div>
        <table className="dense-table w-full">
          <thead>
            <tr>
              <th>Firma</th>
              <th>Anahtar Kelimeler (isim&apos;de aranan)</th>
              <th style={{ width: 140 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={3} className="text-center py-8 text-[color:var(--text-3)]">
                  Yükleniyor…
                </td>
              </tr>
            )}
            {brands.map((b) => {
              const isEditing = editingFirma === b.firma;
              return (
                <tr key={b.firma} data-testid="brand-row">
                  <td className="font-bold">
                    {isEditing ? (
                      <input
                        data-testid="brand-edit-firma"
                        value={editValues.firma}
                        onChange={(e) =>
                          setEditValues((v) => ({ ...v, firma: e.target.value }))
                        }
                        className="w-full h-8 px-2 hard border border-[color:var(--border)] bg-white outline-none focus:border-[color:var(--primary)] font-mono"
                      />
                    ) : (
                      <span className="font-mono">{b.firma}</span>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        data-testid="brand-edit-keywords"
                        value={editValues.keywords}
                        onChange={(e) =>
                          setEditValues((v) => ({
                            ...v,
                            keywords: e.target.value,
                          }))
                        }
                        placeholder="Virgülle ayırın: AGC, ASAHI"
                        className="w-full h-8 px-2 hard border border-[color:var(--border)] bg-white outline-none focus:border-[color:var(--primary)] text-xs font-mono"
                      />
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {(b.keywords || []).map((kw) => (
                          <span
                            key={kw}
                            className="inline-block px-1.5 py-0.5 hard bg-[color:var(--bg)] font-mono text-[10px] font-bold"
                          >
                            {kw}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <div className="flex gap-1">
                        <button
                          data-testid="brand-edit-save"
                          onClick={saveEdit}
                          className="p-1.5 hard bg-[color:var(--success)] text-white hover:opacity-90"
                          title="Kaydet"
                        >
                          <Check size={14} strokeWidth={2.5} />
                        </button>
                        <button
                          onClick={() => setEditingFirma(null)}
                          className="p-1.5 hard border border-[color:var(--border)] hover:bg-[color:var(--bg)]"
                          title="İptal"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-1">
                        <button
                          data-testid="brand-edit-btn"
                          onClick={() => startEdit(b)}
                          className="p-1.5 hard hover:bg-[color:var(--bg)] text-[color:var(--text-2)]"
                          title="Düzenle"
                        >
                          <Edit3 size={14} />
                        </button>
                        <button
                          data-testid="brand-delete-btn"
                          onClick={() => remove(b.firma)}
                          className="p-1.5 hard hover:bg-[color:var(--error-bg)] hover:text-[color:var(--error)]"
                          title="Sil"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <form
        onSubmit={create}
        className="hard border border-[color:var(--border)] bg-[color:var(--surface)] p-5 space-y-4 h-fit"
      >
        <div className="flex items-center gap-2">
          <Plus size={18} className="text-[color:var(--accent)]" />
          <h3 className="font-bold">Yeni Firma</h3>
        </div>
        <div>
          <label className="ticker block mb-1 text-[color:var(--text-2)]">
            Firma Adı *
          </label>
          <input
            data-testid="brand-new-firma"
            required
            value={newFirma}
            onChange={(e) => setNewFirma(e.target.value)}
            placeholder="Örn: PILKINGTON"
            className="w-full h-10 px-3 hard border-2 border-[color:var(--border)] bg-white focus:border-[color:var(--primary)] outline-none font-mono uppercase"
          />
        </div>
        <div>
          <label className="ticker block mb-1 text-[color:var(--text-2)]">
            Anahtar Kelimeler
          </label>
          <input
            data-testid="brand-new-keywords"
            value={newKeywords}
            onChange={(e) => setNewKeywords(e.target.value)}
            placeholder="Virgülle ayırın: PILK, PILKINGTON"
            className="w-full h-10 px-3 hard border-2 border-[color:var(--border)] bg-white focus:border-[color:var(--primary)] outline-none text-xs font-mono"
          />
          <div className="text-[10px] text-[color:var(--text-3)] mt-1">
            Boş bırakılırsa firma adı kullanılır. İsim&apos;de bu keyword&apos;lerden biri geçen camlar otomatik bu firmaya atanır.
          </div>
        </div>
        <button
          type="submit"
          disabled={creating}
          data-testid="brand-new-submit"
          className="w-full h-11 hard bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white font-bold uppercase tracking-wider disabled:opacity-50"
        >
          {creating ? "Ekleniyor…" : "Firma Ekle"}
        </button>
      </form>
    </div>
  );
}
